import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronLeft,
  ChevronsDown,
  ChevronsUp,
  Edit3,
  Eye,
  FolderOpen,
  FolderTree,
  Image as ImageIcon,
  Plus,
  Tags,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { SeoEditor } from "../../components/SeoEditor";
import { FilterPanel, SortableHeader } from "../../components/data-controls";
import {
  ActionMenu,
  ActionMenuItem,
  Badge,
  Button,
  Card,
  Confirm,
  DataTableShell,
  Drawer,
  EmptyState,
  ErrorState,
  FormErrorSummary,
  FormSection,
  IconButton,
  ImageUploader,
  Input,
  Loading,
  Modal,
  Notice,
  PageHeader,
  Pagination,
  SearchBox,
  Select,
  Switch,
  Textarea,
} from "../../components/ui";
import { adminApi, assetUrl } from "../../lib/api";
import {
  brandSchema,
  categorySchema,
  type BrandFormValues,
  type CategoryFormValues,
} from "../../lib/form-schemas";
import { dateTime, number, slugify } from "../../lib/format";
import type { AdminBrand, AdminCategory, SeoMetadata } from "../../types/api";

const PAGE_SIZES = [5, 10, 20, 50, 100];
const emptySeo = (): SeoMetadata => ({ title: "", description: "", canonicalUrl: "", imageUrl: "", index: true, follow: true });
const emptyBrand = (): BrandFormValues => ({ slug: "", name: "", country: "", description: "", imageUrl: "", isFeatured: false, isActive: true, seo: emptySeo() });
const emptyCategory = (): CategoryFormValues => ({ slug: "", name: "", description: "", imageUrl: "", displayOrder: 0, parentId: null, isActive: true, seo: emptySeo() });

export function BrandsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["brands"], queryFn: adminApi.brands.list });
  const [editing, setEditing] = useState<AdminBrand | "new" | null>(null);
  const [viewing, setViewing] = useState<AdminBrand | null>(null);
  const [deleting, setDeleting] = useState<AdminBrand | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "active" | "inactive">("");
  const [featured, setFeatured] = useState<"" | "yes" | "no">("");
  const [sort, setSort] = useState("name-asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const {
    register,
    control,
    reset,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<BrandFormValues>({
    resolver: zodResolver(brandSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: emptyBrand(),
  });

  const open = (brand: AdminBrand | "new") => {
    setEditing(brand);
    reset(brand === "new" ? emptyBrand() : {
      slug: brand.slug,
      name: brand.name,
      country: brand.country,
      description: brand.description,
      imageUrl: brand.imageUrl || "",
      isFeatured: brand.isFeatured,
      isActive: brand.isActive,
      seo: brand.seo || emptySeo(),
    });
  };

  const save = useMutation({
    mutationFn: (form: BrandFormValues) => editing === "new"
      ? adminApi.brands.create(form)
      : adminApi.brands.update((editing as AdminBrand).id, form),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ["brands"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.brands.remove(id),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ["brands"] });
    },
  });

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const items = (query.data || [])
      .filter((brand) => !keyword || `${brand.name} ${brand.country} ${brand.slug}`.toLowerCase().includes(keyword))
      .filter((brand) => !status || (status === "active" ? brand.isActive : !brand.isActive))
      .filter((brand) => !featured || (featured === "yes" ? brand.isFeatured : !brand.isFeatured));

    return [...items].sort((a, b) => {
      if (sort === "products-desc") return b.productCount - a.productCount;
      if (sort === "products-asc") return a.productCount - b.productCount;
      if (sort === "created-desc") return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sort === "created-asc") return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (sort === "name-desc") return b.name.localeCompare(a.name, "fa");
      return a.name.localeCompare(b.name, "fa");
    });
  }, [featured, query.data, search, sort, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const activeFilters = [search, status, featured].filter(Boolean).length;
  const changeSort = (value: string) => { setSort(value); setPage(1); };

  return (
    <>
      <PageHeader
        title="مدیریت برندها"
        description="برندها، وضعیت نمایش، تصویر و داده‌های SEO را با یک جدول یکپارچه مدیریت کنید."
        actions={<Button onClick={() => open("new")}><Plus size={17} /> برند جدید</Button>}
      />

      <FilterPanel
        search={<SearchBox value={search} onChange={(value) => { setSearch(value); setPage(1); }} placeholder="جست‌وجوی نام، کشور یا اسلاگ..." />}
        activeCount={activeFilters}
        totalLabel={`${number(filtered.length)} برند`}
        onClear={() => { setSearch(""); setStatus(""); setFeatured(""); setSort("name-asc"); setPage(1); }}
      >
        <Select label="وضعیت" value={status} onChange={(event) => { setStatus(event.target.value as typeof status); setPage(1); }}>
          <option value="">همه وضعیت‌ها</option>
          <option value="active">فعال</option>
          <option value="inactive">غیرفعال</option>
        </Select>
        <Select label="نمایش ویژه" value={featured} onChange={(event) => { setFeatured(event.target.value as typeof featured); setPage(1); }}>
          <option value="">همه برندها</option>
          <option value="yes">فقط برندهای ویژه</option>
          <option value="no">غیر ویژه</option>
        </Select>
        <Select label="مرتب‌سازی" value={sort} onChange={(event) => changeSort(event.target.value)}>
          <option value="name-asc">نام: الف تا ی</option>
          <option value="name-desc">نام: ی تا الف</option>
          <option value="products-desc">بیشترین محصول</option>
          <option value="products-asc">کمترین محصول</option>
          <option value="created-desc">جدیدترین</option>
          <option value="created-asc">قدیمی‌ترین</option>
        </Select>
      </FilterPanel>

      {query.isLoading ? <Loading /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : !visible.length ? (
        <EmptyState title="برندی پیدا نشد" description="جست‌وجو یا فیلترها را تغییر دهید، یا برند جدیدی ثبت کنید." />
      ) : (
        <DataTableShell
          pagination={(
            <Pagination
              page={safePage}
              totalPages={totalPages}
              totalCount={filtered.length}
              pageSize={pageSize}
              onChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              pageSizeOptions={PAGE_SIZES}
            />
          )}
        >
          <table className="product-table management-table">
            <thead>
              <tr>
                <th>برند</th>
                <SortableHeader label="نام" value={sort} ascValue="name-asc" descValue="name-desc" onChange={changeSort} />
                <th>کشور</th>
                <SortableHeader label="محصولات" value={sort} ascValue="products-asc" descValue="products-desc" defaultDirection="desc" onChange={changeSort} />
                <th>وضعیت</th>
                <SortableHeader label="تاریخ ثبت" value={sort} ascValue="created-asc" descValue="created-desc" defaultDirection="desc" onChange={changeSort} />
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((brand) => (
                <tr key={brand.id}>
                  <td data-label="تصویر"><div className="table-avatar">{brand.imageUrl ? <img src={assetUrl(brand.imageUrl)} alt={brand.name} /> : <Tags />}</div></td>
                  <td data-label="نام">
                    <div className="table-primary__copy">
                      <strong>{brand.name}</strong>
                      <small dir="ltr">/{brand.slug}</small>
                    </div>
                  </td>
                  <td data-label="کشور">{brand.country || "—"}</td>
                  <td data-label="محصولات"><strong>{number(brand.productCount)}</strong> محصول</td>
                  <td data-label="وضعیت">
                    <div className="flex flex-wrap gap-1.5">
                      <Badge value={brand.isActive ? "فعال" : "غیرفعال"} tone={brand.isActive ? "success" : "neutral"} />
                      {brand.isFeatured ? <Badge value="ویژه" tone="warning" /> : null}
                    </div>
                  </td>
                  <td data-label="تاریخ ثبت">{dateTime(brand.createdAt)}</td>
                  <td data-label="عملیات">
                    <ActionMenu>
                      <ActionMenuItem onClick={() => setViewing(brand)}><Eye size={15} /> مشاهده جزئیات</ActionMenuItem>
                      <ActionMenuItem onClick={() => open(brand)}><Edit3 size={15} /> ویرایش برند</ActionMenuItem>
                      <ActionMenuItem danger onClick={() => setDeleting(brand)}><Trash2 size={15} /> حذف برند</ActionMenuItem>
                    </ActionMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>
      )}

      <Drawer
        open={Boolean(viewing)}
        title="جزئیات برند"
        description={viewing ? `${viewing.name} · ${number(viewing.productCount)} محصول` : undefined}
        onClose={() => setViewing(null)}
      >
        {viewing ? (
          <div className="stack">
            <Card>
              <div className="brand-detail-hero">
                <div className="brand-detail-hero__image">{viewing.imageUrl ? <img src={assetUrl(viewing.imageUrl)} alt={viewing.name} /> : <Tags />}</div>
                <div>
                  <h3>{viewing.name}</h3>
                  <span dir="ltr">/{viewing.slug}</span>
                  <div className="flex flex-wrap gap-1.5 mt-2"><Badge value={viewing.isActive ? "فعال" : "غیرفعال"} tone={viewing.isActive ? "success" : "neutral"} />{viewing.isFeatured ? <Badge value="ویژه" tone="warning" /> : null}</div>
                </div>
              </div>
            </Card>
            <Card title="اطلاعات برند">
              <dl className="settings-list">
                <div><dt>کشور مبدا</dt><dd>{viewing.country || "—"}</dd></div>
                <div><dt>تعداد محصولات</dt><dd>{number(viewing.productCount)}</dd></div>
                <div><dt>تاریخ ثبت</dt><dd>{dateTime(viewing.createdAt)}</dd></div>
                <div><dt>وضعیت Index</dt><dd>{viewing.seo?.index ? "Index" : "Noindex"}</dd></div>
              </dl>
              {viewing.description ? <p className="message-body mt-4">{viewing.description}</p> : null}
            </Card>
            <Card title="SEO">
              <dl className="settings-list">
                <div><dt>عنوان SEO</dt><dd>{viewing.seo?.title || "عنوان برند"}</dd></div>
                <div><dt>توضیحات Meta</dt><dd>{viewing.seo?.description || "تنظیم نشده"}</dd></div>
                <div><dt>Canonical</dt><dd dir="ltr">{viewing.seo?.canonicalUrl || "—"}</dd></div>
              </dl>
            </Card>
          </div>
        ) : null}
      </Drawer>

      <Modal open={Boolean(editing)} title={editing === "new" ? "ثبت برند جدید" : "ویرایش برند"} onClose={() => setEditing(null)} wide>
        <form className="stack" onSubmit={handleSubmit((form) => save.mutate(form))} noValidate>
          <FormErrorSummary errors={[errors.name?.message, errors.slug?.message, errors.country?.message, errors.description?.message, errors.seo?.canonicalUrl?.message]} />

          <FormSection title="اطلاعات اصلی" description="نام و شناسه برند باید کوتاه، یکتا و برای کاربر قابل تشخیص باشد.">
            <div className="form-grid">
              <Input
                label="نام برند"
                error={errors.name?.message}
                hint="نام رسمی برند مطابق نمایش فروشگاه."
                {...register("name", {
                  onChange: (event) => {
                    if (editing === "new" && !getValues("slug")) setValue("slug", slugify(String(event.target.value)), { shouldDirty: true, shouldValidate: true });
                  },
                })}
              />
              <Input
                label="اسلاگ"
                dir="ltr"
                error={errors.slug?.message}
                hint="شناسه URL یکتا با حروف انگلیسی کوچک."
                {...register("slug", { onChange: (event) => setValue("slug", slugify(String(event.target.value)), { shouldDirty: true, shouldValidate: true }) })}
              />
              <Input label="کشور مبدا" error={errors.country?.message} hint="مثال: France یا ایران." {...register("country")} />
              <div className="switch-stack">
                <Controller control={control} name="isActive" render={({ field }) => <Switch label="برند فعال" hint="در صفحات عمومی فروشگاه قابل استفاده باشد." checked={field.value} onChange={field.onChange} />} />
                <Controller control={control} name="isFeatured" render={({ field }) => <Switch label="برند ویژه" hint="در بخش‌های منتخب فروشگاه برجسته شود." checked={field.value} onChange={field.onChange} />} />
              </div>
              <Textarea className="span-2" label="معرفی برند" rows={5} error={errors.description?.message} hint="متنی مختصر، معتبر و منحصربه‌فرد برای صفحه برند بنویسید." {...register("description")} />
            </div>
          </FormSection>

          <FormSection title="تصویر برند" description="لوگو یا تصویر نماینده برند را با پس‌زمینه تمیز و کیفیت مناسب بارگذاری کنید.">
            <Controller control={control} name="imageUrl" render={({ field }) => <ImageUploader label="لوگو / تصویر برند" value={field.value} onChange={field.onChange} />} />
          </FormSection>

          <Controller
            control={control}
            name="seo"
            render={({ field }) => (
              <SeoEditor
                value={field.value}
                onChange={field.onChange}
                errors={{
                  title: errors.seo?.title?.message,
                  description: errors.seo?.description?.message,
                  canonicalUrl: errors.seo?.canonicalUrl?.message,
                  imageUrl: errors.seo?.imageUrl?.message,
                }}
              />
            )}
          />

          {save.error ? <Notice tone="warning">{save.error.message}</Notice> : null}
          <div className="form-actions">
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>انصراف</Button>
            <Button type="submit" busy={save.isPending}>ذخیره برند</Button>
          </div>
        </form>
      </Modal>

      <Confirm
        open={Boolean(deleting)}
        title="حذف برند"
        description={deleting?.productCount
          ? `برند «${deleting.name}» دارای ${number(deleting.productCount)} محصول است و تا زمانی که محصول وابسته داشته باشد حذف نمی‌شود.`
          : `برند «${deleting?.name || ""}» حذف شود؟ این عملیات برای برند بدون محصول قابل انجام است.`}
        confirmLabel="حذف برند"
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
      />
    </>
  );
}

type TreeNode = AdminCategory & { children: TreeNode[] };

function buildTree(items: AdminCategory[], sortMode = "tree"): TreeNode[] {
  const map = new Map<string, TreeNode>();
  items.forEach((item) => map.set(item.id, { ...item, children: [] }));
  const roots: TreeNode[] = [];
  map.forEach((item) => {
    const parent = item.parentId ? map.get(item.parentId) : undefined;
    if (parent) parent.children.push(item);
    else roots.push(item);
  });
  const compare = (a: TreeNode, b: TreeNode) => {
    if (sortMode === "name") return a.name.localeCompare(b.name, "fa");
    if (sortMode === "products") return b.productCount - a.productCount || a.name.localeCompare(b.name, "fa");
    if (sortMode === "created") return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    return a.displayOrder - b.displayOrder || a.name.localeCompare(b.name, "fa");
  };
  const sortTree = (nodes: TreeNode[]) => {
    nodes.sort(compare);
    nodes.forEach((node) => sortTree(node.children));
  };
  sortTree(roots);
  return roots;
}

function collectDescendantIds(id: string, items: AdminCategory[]): Set<string> {
  const result = new Set<string>([id]);
  let changed = true;
  while (changed) {
    changed = false;
    items.forEach((item) => {
      if (item.parentId && result.has(item.parentId) && !result.has(item.id)) {
        result.add(item.id);
        changed = true;
      }
    });
  }
  return result;
}

function flattenOptions(nodes: TreeNode[], depth = 0): Array<{ id: string; name: string; depth: number }> {
  return nodes.flatMap((node) => [
    { id: node.id, name: node.name, depth },
    ...flattenOptions(node.children, depth + 1),
  ]);
}

export function CategoriesPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["categories"], queryFn: adminApi.categories.list });
  const [editing, setEditing] = useState<AdminCategory | "new" | null>(null);
  const [viewing, setViewing] = useState<AdminCategory | null>(null);
  const [deleting, setDeleting] = useState<AdminCategory | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "active" | "inactive">("");
  const [scope, setScope] = useState<"" | "root" | "child">("");
  const [sort, setSort] = useState("tree");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const {
    register,
    control,
    reset,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: emptyCategory(),
  });

  const currentParentId = useWatch({ control, name: "parentId" });
  const currentEditingId = editing && editing !== "new" ? editing.id : null;

  const open = (category: AdminCategory | "new") => {
    setEditing(category);
    reset(category === "new" ? emptyCategory() : {
      slug: category.slug,
      name: category.name,
      description: category.description || "",
      imageUrl: category.imageUrl || "",
      displayOrder: category.displayOrder,
      parentId: category.parentId || null,
      isActive: category.isActive,
      seo: category.seo || emptySeo(),
    });
  };

  const save = useMutation({
    mutationFn: (form: CategoryFormValues) => editing === "new"
      ? adminApi.categories.create(form)
      : adminApi.categories.update((editing as AdminCategory).id, form),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });

  const remove = useMutation({
    mutationFn: adminApi.categories.remove,
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
  });

  const allTree = useMemo(() => buildTree(query.data || []), [query.data]);
  const options = useMemo(() => flattenOptions(allTree), [allTree]);
  const categoryMap = useMemo(() => new Map((query.data || []).map((item) => [item.id, item])), [query.data]);

  const items = useMemo(() => {
    const all = query.data || [];
    const byId = new Map(all.map((category) => [category.id, category]));
    const keyword = search.trim().toLowerCase();
    const directlyMatched = all
      .filter((category) => !keyword || `${category.name} ${category.slug} ${category.description || ""}`.toLowerCase().includes(keyword))
      .filter((category) => !status || (status === "active" ? category.isActive : !category.isActive))
      .filter((category) => !scope || (scope === "root" ? !category.parentId : Boolean(category.parentId)));

    if (!keyword && !scope && !status) return directlyMatched;

    const visibleIds = new Set(directlyMatched.map((category) => category.id));
    directlyMatched.forEach((category) => {
      let parentId = category.parentId;
      while (parentId) {
        visibleIds.add(parentId);
        parentId = byId.get(parentId)?.parentId || null;
      }
    });
    return all.filter((category) => visibleIds.has(category.id));
  }, [query.data, scope, search, status]);

  const tree = useMemo(() => buildTree(items, sort), [items, sort]);
  const blockedParentIds = useMemo(() => currentEditingId ? collectDescendantIds(currentEditingId, query.data || []) : new Set<string>(), [currentEditingId, query.data]);
  const childCounts = useMemo(() => {
    const map = new Map<string, number>();
    (query.data || []).forEach((item) => { if (item.parentId) map.set(item.parentId, (map.get(item.parentId) || 0) + 1); });
    return map;
  }, [query.data]);

  const expandableIds = useMemo(() => new Set((query.data || []).filter((item) => (childCounts.get(item.id) || 0) > 0).map((item) => item.id)), [childCounts, query.data]);

  useEffect(() => {
    if (!query.data?.length) return;
    setExpanded(new Set(expandableIds));
  }, [expandableIds, query.data]);

  const expandAll = () => setExpanded(new Set(expandableIds));
  const collapseAll = () => setExpanded(new Set());

  const toggleExpanded = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const renderNode = (node: TreeNode, depth = 0) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = search ? true : expanded.has(node.id);
    return (
      <div className="category-branch" data-depth={depth} key={node.id}>
        <div className="category-row category-row--clean" style={{ "--tree-depth": depth } as CSSProperties}>
          <button type="button" className="tree-toggle" aria-label={hasChildren ? (isExpanded ? "بستن زیرمجموعه" : "نمایش زیرمجموعه") : "بدون زیرمجموعه"} disabled={!hasChildren} onClick={() => hasChildren && toggleExpanded(node.id)}>
            {hasChildren ? (isExpanded ? <ChevronDown /> : <ChevronLeft />) : <span className="tree-leaf-dot" />}
          </button>
          <div className="category-row__icon">{node.imageUrl ? <img className="size-full rounded-xl object-cover" src={assetUrl(node.imageUrl)} alt="" /> : <FolderTree />}</div>
          <button type="button" className="category-row__identity" onClick={() => setViewing(node)}>
            <strong>{node.name}</strong>
            {hasChildren ? <small>{number(node.children.length)} زیرشاخه</small> : <small>دسته نهایی</small>}
          </button>
          <div className="category-row__meta">
            <span className={node.isActive ? "category-status-dot is-active" : "category-status-dot"} title={node.isActive ? "فعال" : "غیرفعال"} />
            <ActionMenu>
              <ActionMenuItem onClick={() => setViewing(node)}><Eye size={15} /> جزئیات</ActionMenuItem>
              <ActionMenuItem onClick={() => open(node)}><Edit3 size={15} /> ویرایش</ActionMenuItem>
              <ActionMenuItem danger onClick={() => setDeleting(node)}><Trash2 size={15} /> حذف</ActionMenuItem>
            </ActionMenu>
          </div>
        </div>
        {hasChildren && isExpanded ? <div className="category-branch__children">{node.children.map((child) => renderNode(child, depth + 1))}</div> : null}
      </div>
    );
  };

  const activeFilters = [search, status, scope].filter(Boolean).length;

  return (
    <>
      <PageHeader
        title="دسته‌بندی‌ها"
        description="دسته‌ها را به‌صورت درختی، قابل جست‌وجو و با کنترل دقیق والد، ترتیب، وضعیت و SEO مدیریت کنید."
        actions={<Button onClick={() => open("new")}><Plus size={17} /> دسته جدید</Button>}
      />

      <FilterPanel
        search={<SearchBox value={search} onChange={setSearch} placeholder="جست‌وجوی دسته، اسلاگ یا توضیحات..." />}
        activeCount={activeFilters}
        totalLabel={`${number(items.length)} دسته`}
        onClear={() => { setSearch(""); setStatus(""); setScope(""); setSort("tree"); }}
      >
        <Select label="وضعیت" value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
          <option value="">همه وضعیت‌ها</option>
          <option value="active">فعال</option>
          <option value="inactive">غیرفعال</option>
        </Select>
        <Select label="سطح دسته" value={scope} onChange={(event) => setScope(event.target.value as typeof scope)}>
          <option value="">همه سطوح</option>
          <option value="root">فقط دسته‌های اصلی</option>
          <option value="child">فقط زیرمجموعه‌ها</option>
        </Select>
        <Select label="نوع نمایش و مرتب‌سازی" value={sort} onChange={(event) => setSort(event.target.value)}>
          <option value="tree">ساختار درختی</option>
          <option value="order">ترتیب نمایش</option>
          <option value="name">نام دسته</option>
          <option value="products">بیشترین محصول</option>
          <option value="created">جدیدترین</option>
        </Select>
      </FilterPanel>

      {query.isLoading ? <Loading /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : !tree.length ? (
        <EmptyState title="دسته‌بندی پیدا نشد" description="فیلترها را پاک کنید یا دسته جدیدی بسازید." />
      ) : (
        <section className="category-tree-shell">
          <div className="category-tree-toolbar">
            <div>
              <strong>نمای درختی دسته‌بندی‌ها</strong>
              <span>{number(tree.length)} شاخه اصلی · {number(items.length)} دسته قابل مشاهده</span>
            </div>
            <div className="category-tree-toolbar__actions">
              <Button type="button" variant="secondary" onClick={expandAll}><ChevronsDown size={16} /> باز کردن همه</Button>
              <Button type="button" variant="ghost" onClick={collapseAll}><ChevronsUp size={16} /> بستن همه</Button>
            </div>
          </div>
          <div className="category-tree">{tree.map((node) => renderNode(node))}</div>
        </section>
      )}

      <Modal open={Boolean(viewing)} title="جزئیات دسته‌بندی" onClose={() => setViewing(null)} wide>
        {viewing ? <div className="category-detail-modal">
          <div className="category-detail-hero">
            <div className="category-detail-hero__image">{viewing.imageUrl ? <img src={assetUrl(viewing.imageUrl)} alt={viewing.name} /> : <FolderTree />}</div>
            <div><h3>{viewing.name}</h3><p dir="ltr">/{viewing.slug}</p><Badge value={viewing.isActive ? "فعال" : "غیرفعال"} tone={viewing.isActive ? "success" : "neutral"} /></div>
          </div>
          <dl className="category-detail-list">
            <div><dt>دسته والد</dt><dd>{viewing.parentId ? categoryMap.get(viewing.parentId)?.name || "—" : "دسته اصلی"}</dd></div>
            <div><dt>سطح ساختار</dt><dd>{number((() => { let level = 1; let parentId = viewing.parentId; while (parentId) { level += 1; parentId = categoryMap.get(parentId)?.parentId || null; } return level; })())}</dd></div>
            <div><dt>محصولات مستقیم</dt><dd>{number(viewing.productCount)} محصول</dd></div>
            <div><dt>زیرشاخه مستقیم</dt><dd>{number(childCounts.get(viewing.id) || 0)} دسته</dd></div>
            <div><dt>ترتیب نمایش</dt><dd>{number(viewing.displayOrder)}</dd></div>
            <div><dt>تاریخ ایجاد</dt><dd>{dateTime(viewing.createdAt)}</dd></div>
          </dl>
          <div className="category-detail-description"><span>توضیحات</span><p>{viewing.description || "توضیحی برای این دسته ثبت نشده است."}</p></div>
          <div className="category-detail-seo"><strong>نمای کلی SEO</strong><p>{viewing.seo?.title || viewing.name}</p><small>{viewing.seo?.description || "توضیحات متای اختصاصی ثبت نشده است."}</small></div>
          <div className="form-actions"><Button type="button" variant="ghost" onClick={() => setViewing(null)}>بستن</Button><Button type="button" onClick={() => { const current = viewing; setViewing(null); open(current); }}><Edit3 size={16} /> ویرایش دسته</Button></div>
        </div> : null}
      </Modal>

      <Modal open={Boolean(editing)} title={editing === "new" ? "ثبت دسته جدید" : "ویرایش دسته‌بندی"} onClose={() => setEditing(null)} wide>
        <form className="stack" onSubmit={handleSubmit((form) => save.mutate({ ...form, parentId: form.parentId || null }))} noValidate>
          <FormErrorSummary errors={[errors.name?.message, errors.slug?.message, errors.displayOrder?.message, errors.seo?.canonicalUrl?.message]} />

          <FormSection title="ساختار دسته" description="نام، والد و ترتیب نمایش را طوری تنظیم کنید که ساختار فروشگاه برای کاربر نهایی قابل فهم باشد.">
            <div className="form-grid">
              <Input
                label="نام دسته"
                error={errors.name?.message}
                hint="نام کوتاه و قابل‌فهم برای مشتری."
                {...register("name", {
                  onChange: (event) => {
                    if (editing === "new" && !getValues("slug")) setValue("slug", slugify(String(event.target.value)), { shouldDirty: true, shouldValidate: true });
                  },
                })}
              />
              <Input label="اسلاگ" dir="ltr" error={errors.slug?.message} hint="شناسه URL یکتا با حروف انگلیسی کوچک." {...register("slug", { onChange: (event) => setValue("slug", slugify(String(event.target.value)), { shouldDirty: true, shouldValidate: true }) })} />
              <Select label="دسته والد" value={currentParentId || ""} hint="فاصله ابتدای هر گزینه، عمق آن دسته را در ساختار درختی نشان می‌دهد." {...register("parentId", { setValueAs: (value) => value || null })}>
                <option value="">بدون والد — دسته اصلی</option>
                {options.filter((option) => !blockedParentIds.has(option.id)).map((option) => <option className="tree-select-option" key={option.id} value={option.id}>{"\u00A0".repeat(option.depth * 6)}{option.name}</option>)}
              </Select>
              <Input label="ترتیب نمایش" type="number" min="0" error={errors.displayOrder?.message} hint="عدد کمتر، زودتر در همان سطح نمایش داده می‌شود." {...register("displayOrder", { valueAsNumber: true })} />
              <Textarea className="span-2" label="توضیحات دسته" rows={5} error={errors.description?.message} hint="توضیح منحصربه‌فرد و مفید برای صفحه دسته بنویسید." {...register("description")} />
            </div>
          </FormSection>

          <FormSection title="نمایش و تصویر" description="تصویر دسته و وضعیت انتشار آن را کنترل کنید.">
            <div className="form-grid">
              <div className="span-2"><Controller control={control} name="imageUrl" render={({ field }) => <ImageUploader label="تصویر دسته" value={field.value} hint="تصویر نماینده دسته با نسبت مناسب." onChange={field.onChange} />} /></div>
              <div className="span-2"><Controller control={control} name="isActive" render={({ field }) => <Switch label="دسته فعال" checked={field.value} hint="دسته غیرفعال در فروشگاه نمایش داده نمی‌شود." onChange={field.onChange} />} /></div>
            </div>
          </FormSection>

          <Controller
            control={control}
            name="seo"
            render={({ field }) => (
              <SeoEditor
                value={field.value}
                onChange={field.onChange}
                errors={{ title: errors.seo?.title?.message, description: errors.seo?.description?.message, canonicalUrl: errors.seo?.canonicalUrl?.message, imageUrl: errors.seo?.imageUrl?.message }}
              />
            )}
          />

          {save.error ? <Notice tone="warning">{save.error.message}</Notice> : null}
          <div className="form-actions">
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>انصراف</Button>
            <Button type="submit" busy={save.isPending}>ذخیره دسته‌بندی</Button>
          </div>
        </form>
      </Modal>

      <Confirm
        open={Boolean(deleting)}
        title="حذف دسته‌بندی"
        description={deleting
          ? `دسته «${deleting.name}» ${childCounts.get(deleting.id) ? `${number(childCounts.get(deleting.id) || 0)} زیرشاخه دارد و قبل از حذف باید زیرشاخه‌ها منتقل یا حذف شوند.` : deleting.productCount ? `${number(deleting.productCount)} محصول مرتبط دارد. حذف دسته، محصول را حذف نمی‌کند اما باید وابستگی‌ها از سمت API بررسی شوند.` : "فاقد زیرشاخه و محصول مستقیم است و می‌تواند حذف شود."}`
          : ""}
        confirmLabel="حذف دسته"
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
      />
    </>
  );
}
